import { z } from 'zod';
import { t } from '@/i18n';
import { NotificationDirectType, ApplicationComponentId } from '@/model';
import { NOTIFICATION_TARGET_COMPONENTS } from '@/model/application-component';

export const modalFormSchema = z
  .object({
    title: z.string().min(1, 'O título é obrigatório').max(50, 'O título deve ter no máximo 50 caracteres'),
    content: z.string().min(1, 'O conteúdo é obrigatório').max(100, 'O conteúdo deve ter no máximo 100 caracteres'),
    direct: z.boolean().optional(),
    direct_type: z.enum([NotificationDirectType.APP_AREA, NotificationDirectType.EXTERNAL_LINK]).optional(),
    screen: z.string().optional(), // Alterado para string opcional
    hyperlink: z.string().optional(),
    has_scheduled_at: z.boolean().optional(),
    scheduled_at: z.string().optional(),
  })
  .superRefine((values, ctx) => {
    if (values.has_scheduled_at) {
      if (!values.scheduled_at) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['scheduled_at'],
          message: t(
            'pages.communication_central.components.notification_create_modal.schema.scheduled_at_required',
          ),
        });
      } else if (Number.isNaN(new Date(values.scheduled_at).getTime())) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['scheduled_at'],
          message: t(
            'pages.communication_central.components.notification_create_modal.schema.scheduled_at_invalid',
          ),
        });
      } else if (new Date(values.scheduled_at).getTime() <= Date.now()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['scheduled_at'],
          message: t(
            'pages.communication_central.components.notification_create_modal.schema.scheduled_at_future',
          ),
        });
      }
    } else if (values.scheduled_at) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['has_scheduled_at'],
        message: t(
          'pages.communication_central.components.notification_create_modal.schema.scheduled_at_should_be_empty',
        ),
      });
    }

    if (values.direct) {
      if (!values.direct_type) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['direct_type'],
          message: 'Selecione um tipo para direcionar',
        });
      } else if (values.direct_type === NotificationDirectType.APP_AREA) {
        if (!values.screen) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['screen'],
            message: 'Selecione uma área do aplicativo',
          });
        } else if (!NOTIFICATION_TARGET_COMPONENTS.includes(values.screen as ApplicationComponentId)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['screen'],
            message: 'Selecione uma área válida do aplicativo',
          });
        }
        // Garante que hyperlink seja ignorado
        if (values.hyperlink) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['hyperlink'],
            message: 'O link deve ser vazio para área do aplicativo',
          });
        }
      } else if (values.direct_type === NotificationDirectType.EXTERNAL_LINK) {
        if (!values.hyperlink) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['hyperlink'],
            message: 'Insira um link',
          });
        } else {
          // Validação de URL apenas para EXTERNAL_LINK
          const urlRegex = /^(https?:\/\/)?([\w-]+(\.[\w-]+)+\/?|localhost)(:\d+)?([/\\?#].*)?$/i;
          if (!urlRegex.test(values.hyperlink)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['hyperlink'],
              message: 'Insira um link válido',
            });
          }
        }
        // Garante que screen seja ignorado
        if (values.screen) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['screen'],
            message: 'A área do aplicativo deve ser vazia para link externo',
          });
        }
      }
    } else {
      // Garante que os campos sejam ignorados quando direct é false
      if (values.direct_type || values.screen || values.hyperlink) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['direct'],
          message: 'Campos de direcionamento devem ser vazios quando direcionamento está desativado',
        });
      }
    }
  });

export type ModalFormData = z.infer<typeof modalFormSchema>;
